import { useRef, useState } from 'react'
import { useTours } from '../state/tours'
import { useAuth } from '../state/auth'
import { useUi } from '../state/ui'
import type { Tour, TourKind } from '../lib/models'
import { ascentDescent, formatDuration, formatKm, lineDistanceMeters } from '../lib/geo'
import { parseGpx } from '../lib/gpx'
import TourDetail from './TourDetail'
import { SPORT_ICONS } from '../App'
import { IconDownload, IconTours } from './icons'

export default function ToursPanel() {
  const { tours, openId, open, syncing, lastSyncError } = useTours()
  const authStatus = useAuth((s) => s.status)
  const showToast = useUi((s) => s.showToast)
  const [filter, setFilter] = useState<'all' | TourKind>('all')
  const fileRef = useRef<HTMLInputElement>(null)

  const openTour = tours.find((t) => t.id === openId)
  if (openTour) return <TourDetail tour={openTour} />

  const filtered = filter === 'all' ? tours : tours.filter((t) => t.kind === filter)

  const importGpx = async (file: File) => {
    try {
      const track = parseGpx(await file.text())
      const { ascent, descent } = ascentDescent(track.coords.map((c) => c[2]), 3)
      const distanceM = lineDistanceMeters(track.coords)
      const durationS = track.times ? (track.times[track.times.length - 1] - track.times[0]) / 1000 : 0
      const tour: Tour = {
        id: crypto.randomUUID(),
        name: track.name,
        sport: 'hike',
        kind: track.times ? 'recorded' : 'planned',
        data: {
          coords: track.coords,
          times: track.times,
          waypoints: track.waypoints?.map((w) => ({ lng: w.lng, lat: w.lat, name: w.name })),
          stats: { distanceM, ascentM: ascent, descentM: descent, durationS },
        },
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      await useTours.getState().save(tour)
      showToast(`Imported “${track.name}” (${formatKm(distanceM)})`)
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Could not read this GPX file')
    }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, flex: 1 }}>Your tours</h2>
        <button className="chip" onClick={() => fileRef.current?.click()}>
          <IconDownload style={{ transform: 'rotate(180deg)' }} /> Import GPX
        </button>
        {/* no `accept`: iOS greys out .gpx in the picker for unknown types — we validate the content instead */}
        <input
          ref={fileRef}
          type="file"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void importGpx(f)
            e.target.value = ''
          }}
        />
      </div>

      <div className="seg" style={{ marginBottom: 14 }}>
        {(['all', 'planned', 'recorded'] as const).map((f) => (
          <button key={f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
            {f === 'all' ? 'All' : f === 'planned' ? 'Planned' : 'Recorded'}
          </button>
        ))}
      </div>

      {authStatus !== 'in' && tours.length > 0 && (
        <div className="hint">
          Tours live on this device. <b>Sign in</b> from the Profile tab to back them up and sync everywhere.
        </div>
      )}
      {syncing && (
        <p className="muted" style={{ margin: '6px 2px' }}>
          <span className="busy-dot" /> Syncing…
        </p>
      )}
      {lastSyncError && authStatus === 'in' && <p className="error-text">Sync issue: {lastSyncError}</p>}

      {filtered.length === 0 && (
        <div className="empty">
          <div className="e-icon">
            <IconTours />
          </div>
          <div className="e-title">No tours yet</div>
          <div className="e-sub">
            Plan a route and save it, hit the orange button to record an activity, or import a GPX file.
          </div>
        </div>
      )}

      {filtered.map((t) => {
        const Icon = SPORT_ICONS[t.sport] ?? SPORT_ICONS.hike
        return (
          <button key={t.id} className={`tour-card${t.kind === 'recorded' ? ' rec' : ''}`} onClick={() => open(t.id)}>
            <div className="t-top">
              <span className="t-ico">
                <Icon />
              </span>
              <span className="t-name">{t.name}</span>
              <span className={`badge${t.kind === 'recorded' ? ' rec' : ''}`}>{t.kind}</span>
            </div>
            <div className="t-meta">
              <span>{formatKm(t.data.stats.distanceM)}</span>
              <span>↗ {t.data.stats.ascentM} m</span>
              {t.data.stats.durationS > 0 && <span>{formatDuration(t.data.stats.durationS)}</span>}
              <span style={{ marginLeft: 'auto', color: 'var(--ink-3)' }}>
                {new Date(t.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
              </span>
            </div>
          </button>
        )
      })}
    </>
  )
}
